import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToHtmlProps {}

// HTML theme options
type HtmlTheme = 'light' | 'dark' | 'colorful' | 'minimal';

// HTML content display options
type DisplayOption = 'expanded' | 'collapsed';

const JsonToHtml: React.FC<JsonToHtmlProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the HTML output
  const [htmlOutput, setHtmlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for HTML theme
  const [theme, setTheme] = useState<HtmlTheme>('light');
  // State for display option
  const [displayOption, setDisplayOption] = useState<DisplayOption>('expanded');
  // State to track if the conversion happened
  const [isHtmlGenerated, setIsHtmlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for HTML preview
  const [showPreview, setShowPreview] = useState<boolean>(true);


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsHtmlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsHtmlGenerated(false);
    setHtmlOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setHtmlOutput('');
    setError('');
    setFileName('json_data');
    setTheme('light');
    setDisplayOption('expanded');
    setIsHtmlGenerated(false);
  };

  // Get CSS for the selected theme
  const getThemeStyles = (): string => {
    switch (theme) {
      case 'dark':
        return `
          body { background-color: #1a1a1a; color: #f5f5f5; font-family: Arial, sans-serif; padding: 20px; }
          .json-container { font-family: 'Courier New', monospace; }
          .json-key { color: #88c0d0; }
          .json-string { color: #a3be8c; }
          .json-number { color: #b48ead; }
          .json-boolean { color: #ebcb8b; }
          .json-null { color: #bf616a; }
          .json-bracket { color: #81a1c1; }
          .collapsible { cursor: pointer; user-select: none; }
          .collapsible::before { content: '-'; margin-right: 5px; display: inline-block; }
          .collapsed::before { content: '+'; }
          .collapsed + ul, .collapsed + ol { display: none; }
          ul, ol { list-style-type: none; margin: 0; padding-left: 20px; }
        `;
      case 'colorful':
        return `
          body { background-color: #ffffff; color: #333; font-family: Arial, sans-serif; padding: 20px; }
          .json-container { font-family: 'Courier New', monospace; background-color: #f8f9fa; padding: 15px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .json-key { color: #e83e8c; font-weight: bold; }
          .json-string { color: #28a745; }
          .json-number { color: #fd7e14; }
          .json-boolean { color: #007bff; }
          .json-null { color: #6c757d; }
          .json-bracket { color: #dc3545; font-weight: bold; }
          .collapsible { cursor: pointer; user-select: none; background-color: #f1f3f5; padding: 2px 5px; border-radius: 4px; }
          .collapsible::before { content: '▼'; margin-right: 5px; display: inline-block; font-size: 10px; }
          .collapsed::before { content: '►'; }
          .collapsed + ul, .collapsed + ol { display: none; }
          ul, ol { list-style-type: none; margin: 0; padding-left: 20px; border-left: 1px dashed #dee2e6; }
        `;
      case 'minimal':
        return `
          body { background-color: #ffffff; color: #333; font-family: Arial, sans-serif; padding: 20px; }
          .json-container { font-family: 'Courier New', monospace; }
          .json-key { font-weight: bold; }
          .collapsible { cursor: pointer; user-select: none; }
          .collapsible::before { content: '-'; margin-right: 5px; display: inline-block; }
          .collapsed::before { content: '+'; }
          .collapsed + ul, .collapsed + ol { display: none; }
          ul, ol { list-style-type: none; margin: 0; padding-left: 20px; }
        `;
      default: // light theme
        return `
          body { background-color: #ffffff; color: #333; font-family: Arial, sans-serif; padding: 20px; }
          .json-container { font-family: 'Courier New', monospace; background-color: #f8f9fa; padding: 15px; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
          .json-key { color: #0056b3; }
          .json-string { color: #008000; }
          .json-number { color: #d63384; }
          .json-boolean { color: #9c27b0; }
          .json-null { color: #6c757d; }
          .json-bracket { color: #495057; }
          .collapsible { cursor: pointer; user-select: none; }
          .collapsible::before { content: '-'; margin-right: 5px; display: inline-block; }
          .collapsed::before { content: '+'; }
          .collapsed + ul, .collapsed + ol { display: none; }
          ul, ol { list-style-type: none; margin: 0; padding-left: 20px; }
        `;
    }
  };

  // Generate HTML representation of JSON object
  const generateJsonHtml = (obj: any, isCollapsed: boolean = false): string => {
    if (obj === null) {
      return '<span class="json-null">null</span>';
    }

    if (typeof obj === 'undefined') {
      return '<span class="json-null">undefined</span>';
    }

    if (typeof obj === 'string') {
      return `<span class="json-string">"${obj.replace(/"/g, '\\"').replace(/</g, '&lt;').replace(/>/g, '&gt;')}"</span>`;
    }

    if (typeof obj === 'number') {
      return `<span class="json-number">${obj}</span>`;
    }

    if (typeof obj === 'boolean') {
      return `<span class="json-boolean">${obj}</span>`;
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '<span class="json-bracket">[]</span>';
      }

      const collapsibleClass = isCollapsed ? 'collapsible collapsed' : 'collapsible';
      let html = `<span class="${collapsibleClass} json-bracket">[</span><ol>`;
      
      obj.forEach((item, index) => {
        html += `<li>${generateJsonHtml(item, displayOption === 'collapsed')}${index < obj.length - 1 ? ',' : ''}</li>`;
      });
      
      html += '</ol><span class="json-bracket">]</span>';
      return html;
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      
      if (keys.length === 0) {
        return '<span class="json-bracket">{}</span>';
      }

      const collapsibleClass = isCollapsed ? 'collapsible collapsed' : 'collapsible';
      let html = `<span class="${collapsibleClass} json-bracket">{</span><ul>`;
      
      keys.forEach((key, index) => {
        html += `<li><span class="json-key">"${key}"</span>: ${generateJsonHtml(obj[key], displayOption === 'collapsed')}${index < keys.length - 1 ? ',' : ''}</li>`;
      });
      
      html += '</ul><span class="json-bracket">}</span>';
      return html;
    }

    return String(obj);
  };

  // Add JavaScript for collapsible functionality
  const getCollapsibleScript = (): string => {
    return `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          let collapsibles = document.querySelectorAll('.collapsible');
          
          collapsibles.forEach(function(element) {
            element.addEventListener('click', function(e) {
              e.stopPropagation();
              this.classList.toggle('collapsed');
            });
          });
        });
      </script>
    `;
  };

  // Convert JSON to HTML
  const convertJsonToHtml = () => {
    setError('');
    setIsLoading(true);
    setIsHtmlGenerated(false);
    setHtmlOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      const jsonHtml = generateJsonHtml(jsonData, displayOption === 'collapsed');
      const styles = getThemeStyles();
      const script = getCollapsibleScript();
      
      const fullHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${fileName || 'JSON Viewer'}</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="json-container">
            ${jsonHtml}
          </div>
          ${script}
        </body>
        </html>
      `;
      
      setHtmlOutput(fullHtml);
      setIsHtmlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating HTML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy HTML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download HTML file
  const downloadHtml = () => {
    const blob = new Blob([htmlOutput], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_data'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to HTML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to interactive HTML documents. Paste your JSON data below to generate a formatted, collapsible HTML representation.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                    HTML Theme
                  </label>
                  <select
                    id="theme"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as HtmlTheme)}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="colorful">Colorful</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="displayOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Initial Display
                  </label>
                  <select
                    id="displayOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={displayOption}
                    onChange={(e) => setDisplayOption(e.target.value as DisplayOption)}
                  >
                    <option value="expanded">Expanded (show all)</option>
                    <option value="collapsed">Collapsed (hide nested content)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Users can click on brackets to expand/collapse content in the generated HTML
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToHtml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to HTML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              HTML Output
            </label>
            
            {isHtmlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy HTML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadHtml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isHtmlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    HTML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isHtmlGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <button
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide Preview
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3">
                      <iframe
                        srcDoc={htmlOutput}
                        title="HTML Preview"
                        className="w-full h-full"
                        sandbox="allow-scripts"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => setShowPreview(true)}
                    >
                      Show Preview
                    </button>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="htmlOutput" className="text-sm font-medium text-gray-700 mb-1">
                    HTML Code:
                  </label>
                  <textarea
                    id="htmlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={htmlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isHtmlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">HTML Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated HTML includes interactive features - users can click on brackets to expand or collapse nested content.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to HTML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into interactive HTML documents. The converter preserves your JSON structure
              while adding color highlighting and collapsible sections for better readability.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Interactive Visualization</strong> - Collapsible sections for nested objects and arrays</li>
              <li><strong>Syntax Highlighting</strong> - Color-coded JSON elements for better readability</li>
              <li><strong>Multiple Themes</strong> - Choose from light, dark, colorful, or minimal themes</li>
              <li><strong>Initial Display Options</strong> - Start with all content expanded or collapsed</li>
              <li><strong>Live Preview</strong> - See how the HTML will look before downloading</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Choose the "Dark" theme for better visibility when viewing data in low-light environments</li>
              <li>For large JSON structures, use the "Collapsed" display option to make navigation easier</li>
              <li>The generated HTML file can be opened in any web browser and works without internet connection</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToHtml;